import { AD_POSITION } from "@/constants/ads";
import { MARK } from "@/constants/customPageSizeMark";
import { RENDERCARDTYPE } from "@/constants/renderCardType";
import useAppStore from "@/context/store";
import SearchEmpty from "@/pageComponents/Search/SearchEmpty";
import { setSearch } from "@/tracker/shared/global";
import { ConcurrencyController } from "@/utils/concurrencyController";
import filterUrlParams from "@/utils/filterUrlParams";
import { getPageSize } from "@/utils/getPageSize";
import { useMemoizedFn } from "ahooks";
import { isEmpty, uniqBy } from "lodash";
import { useRouter } from "next/router";
import { useEffect, useMemo, useRef, useState } from "react";
import { Else, If, Then, When } from "react-if";
import PinList from "../../components/PinList";
import {
  getAds,
  getDeliveryDetail,
  getSearchPinsByQingzhou,
} from "../../services";
import { RelationWords } from "../Search/RelationWords";
import FilterToggleButton from "../Search/SearchFilter/FilterToggleButton";
import styles from "./index.module.less";
import RecallSearch from "./recallSearch";

export default function SearchPinList({
  query,
  searchResult,
  setRecommend,
  type,
  showMaterialTag = true,
  recommend,
  recommendResultTest,
  recommendTopicListColNum = 3,
  isFilterPanelVisible,
  recommendSearchWords = [],
}) {
  const pageRef = useRef(searchResult ? 2 : 1);
  const randomNumRef = useRef(null);
  const [hasMore, setHasMore] = useState(true);
  const [dataSource, setDataSource] = useState(searchResult || []);
  const [isAllMatch, setIsAllMatch] = useState(true);
  const [relationList, setRelationList] = useState([]);
  const [isRecallSearch, setIsRecalSearch] = useState(false);
  const [splitWords, setSplitWords] = useState([]);
  const [ads, setAds] = useState([]);
  const [questionaire, setQuestionaire] = useState(null);
  const router = useRouter();
  const pathname = router.pathname;
  const initRef = useRef(!searchResult);
  const { user, column: columnNum } = useAppStore(({ user, column }) => ({
    user,
    column,
  }));
  const { q, sort, filterIds } = query;
  const [busy, setBusy] = useState(false);
  const [extraPinQueue, setExtraPinQueue] = useState([]);

  const isRecommendTopicCollect = useMemo(() => {
    return (
      recommendResultTest.value === "string_1" ||
      recommendResultTest.value === "string_2" ||
      recommendResultTest.value === "string_3" ||
      recommendResultTest.value === "string_4"
    );
  }, [recommendResultTest.value]);

  const filterIdMap = useMemo(() => {
    let map = null;
    try {
      if (filterIds) {
        map = filterUrlParams.decode(filterIds);
      }
    } catch (e) {
      console.error("filter_ids对象转换失败");
      return {};
    }
    return map;
  }, [filterIds]);

  const category = useMemo(() => {
    return Object.values(filterIdMap || {}).reduce((result, value) => {
      if (result) {
        return result;
      } else {
        const match = value.match(/category:(.*)/);
        return match && match[1];
      }
    }, "");
  }, [filterIdMap]);

  const queryAction = useMemo(() => {
    // 服务端渲染时，不做初始化的一些操作
    if (initRef.current) {
      pageRef.current = 1;
      setHasMore(true);
      setDataSource([]);
    } else {
      initRef.current = true;
    }
    return async () => {
      if (!q) {
        return;
      }
      if (pageRef.current === 1) {
        setIsRecalSearch(false);
      }

      const filter_ids = filterIdMap
        ? Object.values(filterIdMap).reduce((result, value) => {
            result.push(value);
            return result;
          }, [])
        : null;

      // 设置如果超过30秒，显示服务器繁忙，请稍等片刻...
      const timer = setTimeout(() => {
        setBusy(true);
      }, 10000);

      const [result, waterfallAd] = await Promise.all([
        getSearchPinsByQingzhou({
          text: q,
          ...(sort ? { sort } : null),
          ...(filter_ids ? { filter_ids } : null),
          ...(pathname === "/topic-pin/[keyword]"
            ? { all_match: false }
            : null),
          colors: filterIdMap?.colors?.split(",").pop(),
          limit: getPageSize(MARK.SEARCHPIN),
          page: pageRef.current,
          position: "search_pin",
        }),
        pageRef.current > 1 // 第一页不展示广告，故不请求
          ? getAds({
              positions: [AD_POSITION.SEARCH_WATERFALL_PER_PAGE],
              variables: {
                user_tag: ["pin"],
                category: [category || "none"],
                search_keyword: [q],
              },
            })
          : null,
      ]);

      // 如果结果返回有问题直接设置繁忙，如果没问题就清楚定时器
      if (result?.err) {
        setBusy(true);
      } else {
        clearTimeout(timer);
        setBusy(false);
      }

      const pins = result?.pins || [];

      // 搜索推荐主题， 仅在第一页请求更新数据
      if (pageRef.current === 1) {
        setRecommend(result.rec_topic_material);
        setRelationList(result.relations || []);
        if (result.rec_topic_material?.recommendType === "material") {
          setExtraPinQueue(result.rec_topic_material.recommendMaterial);
        }
        // 保存搜索结果数量

        // 设置搜索结果数量到全局状态
        setSearch({
          search_result_number: result.total || 0,
        });
      }

      if (waterfallAd?.[0] && pins.length > 0) {
        //追加到列表前
        pins.unshift({
          ...waterfallAd[0],
          pin_id: `recommend-${waterfallAd[0].material.id}-${pageRef.current}`,
          type: "recommend",
        });
      }

      /**
       * 展示部分匹配的情况
       * 1.素材数量<=30分词数>1,且用户登陆了
       * 2.素材数量<=30分词数>1,用户没登陆，且素材数量为0
       */
      if (
        result.total <= 30 &&
        result.split_words?.length > 1 &&
        // 使用userid是为了避免user对象改变导致搜索结果页刷新
        (user?.user_id || (!user?.user_id && !pins.length))
      ) {
        setIsRecalSearch(true);
        // setHasMore(false);
        setSplitWords(result.split_words);
      }
      if (pins.length <= 0) {
        setHasMore(false);
      }

      // 搜索结果也第N屏后插入主题
      if (result.topics) {
        const recommendTopics = result.topics
          .map((item) => {
            if (isRecommendTopicCollect) {
              return {
                ...item,
                pin_id: `topic_${item.filter_id}`,
                type: RENDERCARDTYPE.topicCollectionA,
              };
            } else {
              return {
                ...item,
                pin_id: `topic_${item.filter_id}`,
                type: RENDERCARDTYPE.topicCollection,
              };
            }
          })
          // 把没有采集的过滤掉，避免展示异常
          .filter((item) => item?.pins?.length > 0);

        if (recommendTopics[0]) {
          // 第一个放到最前面
          pins.unshift(recommendTopics[0]);
        }

        if (recommendTopics[1]) {
          //第二个放到中间位置
          pins.splice(Math.ceil(pins.length / 2), 0, recommendTopics[1]);
        }
      }

      setDataSource((dataSource) => dataSource.concat(pins));
    };
  }, [
    q,
    filterIdMap,
    sort,
    pathname,
    user?.user_id,
    setRecommend,
    isRecommendTopicCollect,
    category,
  ]);

  const queryRecommend = useMemo(() => {
    return async () => {
      const result = await getAds({
        positions: [
          AD_POSITION.SEARCH_WATERFALL_FIXED_1,
          AD_POSITION.SEARCH_WATERFALL_FIXED_2,
          AD_POSITION.SEARCH_WATERFALL_RANDOM_1,
          AD_POSITION.SEARCH_WATERFALL_4,
        ],
        variables: {
          user_tag: ["pin"],
          category: [category || "none"],
          search_keyword: [q],
        },
      });

      setAds(
        result?.reduce((result, item) => {
          if (item) {
            result.push({
              ...item,
              pin_id: `recommend-${item.material.id}`,
              type: "recommend",
            });
          } else {
            result.push(null);
          }
          return result;
        }, []) || []
      );
    };
  }, [category, q]);

  const queryQuestionaire = useMemo(() => {
    return async () => {
      const result = await getDeliveryDetail("web-search-questionaire");
      setQuestionaire(
        result?.pit_modules?.[0]?.material
          ? {
              type: RENDERCARDTYPE.questionaire,
              pin_id: `questionaire-${result?.pit_modules?.[0]?.module_id}`,
              id: result?.pit_modules?.[0]?.module_id,
              name: result?.pit_modules?.[0]?.module_name,
              search_word: q,
              ...result?.pit_modules?.[0]?.material,
            }
          : null
      );
    };
  }, [q]);

  // 创建串行任务队列
  const queryTask = useRef(new ConcurrencyController(1));

  useEffect(() => {
    queryTask.current.run(queryAction);
  }, [queryAction]);

  useEffect(() => {
    queryRecommend();
  }, [queryRecommend]);

  useEffect(() => {
    queryQuestionaire();
  }, [queryQuestionaire]);

  const onLoadMore = useMemoizedFn(async () => {
    queryTask.current.run(async () => {
      pageRef.current += 1;
      await queryAction();
    });
  });

  // 推荐专题列表
  const recommendTopicList = useMemo(() => {
    if (!recommend || isEmpty(recommend)) {
      return [];
    }

    if (
      recommendResultTest.value === "string_3" ||
      recommendResultTest.value === "string_4"
    ) {
      return [
        {
          ...recommend,
          mergeCol: recommendTopicListColNum,
          pin_id: `recommend-topic-${recommend?.[0]?.code}`,
          type: RENDERCARDTYPE.recommendTopicList,
        },
      ];
    }

    return [];
  }, [recommendTopicListColNum, recommend, recommendResultTest.value]);

  const recommendMiddle = useMemo(() => {
    if (!recommend || isEmpty(recommend)) {
      return [];
    }
    if (
      recommendResultTest.value === "string_1" ||
      recommendResultTest.value === "string_2"
    ) {
      return [
        {
          ...recommend,
          pin_id: `recommend-middle-${recommend?.[0]?.code}`,
          type: RENDERCARDTYPE.recommendMiddle,
          mergeCol: columnNum,
          isBanner: true,
        },
      ];
    }

    return [];
  }, [recommend, recommendResultTest.value, columnNum]);

  const recommendSearchWordsB = useMemo(() => {
    // 只有当有推荐搜索词数据时才显示
    if (!recommendSearchWords || recommendSearchWords.length === 0) {
      return [];
    }

    // 将推荐搜索词按5个为一组进行分割
    const chunkSize = 5;
    const chunks = [];
    for (let i = 0; i < recommendSearchWords.length; i += chunkSize) {
      chunks.push(recommendSearchWords.slice(i, i + chunkSize));
    }

    // 为每组创建一个组件对象，只渲染满5个搜索词的组合
    return chunks
      .filter((chunk) => chunk.length === 5) // 🎯 只保留正好5个搜索词的组合
      .map((chunk, index) => ({
        pin_id: `recommend-search-words-${q}-group-${index}`,
        type: RENDERCARDTYPE.recommendSearchWordsB,
        mergeCol: Math.min(columnNum, 5),
        isBanner: true,
        recommendSearchWords: chunk,
        currentTabType: type, // 传递当前tab类型，用于选择对应的图片URL
        groupIndex: index, // 添加组索引，用于区分不同组
      }));
  }, [recommendSearchWords, columnNum, q, type]);

  const list = useMemo(() => {
    if (dataSource.length <= 0 || (ads.length <= 0 && !questionaire)) {
      return dataSource;
    }

    if (randomNumRef.current === null) {
      // 随机 11-20 的位置
      randomNumRef.current = parseInt(Math.random() * 10) + 11;
    }

    const num = randomNumRef.current;

    const list = [
      ...(recommendTopicList || []),
      ...ads.slice(0, 1).filter((i) => i),
      ...dataSource.slice(0, num),
    ];

    const newList1 = [
      ...list.slice(0, 1),
      ...ads.slice(1, 2).filter((i) => i),
      ...list.slice(1),
      ...ads.slice(2, 3).filter((i) => i),
      ...dataSource.slice(num),
    ];

    const newList2 = [
      ...newList1.slice(0, 3),
      ...ads.slice(3, 4).filter((i) => i),
      ...newList1.slice(3),
    ];

    if (newList2.length > 60 && questionaire) {
      newList2.splice(60, 0, questionaire);
    }

    // 🔧 关键修复：先去重，避免重复数据影响推荐搜索词插入位置
    // 先去重，移除重复的pin_id
    let deduplicatedList = uniqBy(newList2, "pin_id");

    // 分组插入推荐搜索词，基于去重后的列表长度来判断插入位置
    if (recommendSearchWordsB.length > 0) {
      // 从后往前插入，避免索引偏移问题
      for (let i = recommendSearchWordsB.length - 1; i >= 0; i--) {
        const targetPosition = 41 + i * 30; // 第一组在第41位，第二组在第71位，第三组在第101位...

        // 基于去重后的列表长度判断是否可以插入
        if (deduplicatedList.length >= targetPosition) {
          // 直接使用目标位置，因为已经基于去重后的列表
          const actualInsertPosition = targetPosition;

          // 确保插入位置不超过当前列表长度
          if (actualInsertPosition <= deduplicatedList.length) {
            deduplicatedList.splice(
              actualInsertPosition,
              0,
              recommendSearchWordsB[i]
            );
          }
        }
      }
    }

    // 使用已经去重并插入了推荐搜索词的列表
    const finalList = deduplicatedList;

    if (finalList.length > 12 * columnNum && recommendMiddle.length > 0) {
      finalList.splice(12 * columnNum, 0, ...recommendMiddle);
    }

    return finalList;
  }, [
    dataSource,
    ads,
    questionaire,
    columnNum,
    recommendMiddle,
    recommendSearchWordsB,
    recommendTopicList,
  ]);

  return (
    <div className={styles.pin} data-search-word={q}>
      {isFilterPanelVisible ? (
        <FilterToggleButton relationList={relationList} />
      ) : (
        <When condition={type === "pin" && !!relationList?.length}>
          <RelationWords relationList={relationList} />
        </When>
      )}
      <If condition={dataSource.length > 0 || hasMore}>
        <Then>
          {/* 用来控制pinQueue的值， 控制详情弹窗的下一张取值正确        
            1.如果用户点击全匹配的则pinQueue为全匹配的
            2.如果用户点击部分匹配的则pinQueue的数据为部分匹配
          */}
          <div
            onClick={() => {
              setIsAllMatch(true);
            }}
          >
            <PinList
              isSearch={true}
              searchWord={query?.q}
              dataSource={list}
              hasMore={hasMore}
              onLoadMore={onLoadMore}
              loadingContent={
                busy ? (
                  <div className={styles.busy}>服务器繁忙，请稍等片刻...</div>
                ) : null
              }
              endMessage={isRecallSearch ? "" : undefined}
              disableQueue={!isAllMatch}
              showMaterialTag={showMaterialTag}
              extraPinQueue={extraPinQueue}
            />
          </div>
        </Then>
        <Else>
          {!isRecallSearch && (
            <div style={{ marginTop: "48px" }}>
              <SearchEmpty></SearchEmpty>
            </div>
          )}
        </Else>
      </If>
      {/* abtest 基于部分匹配召回搜索的前端交互优化 */}
      <When condition={isRecallSearch && !hasMore}>
        <RecallSearch
          splitWords={splitWords}
          query={query}
          isAllMatch={isAllMatch}
          haveData={!!dataSource.length}
          type={type}
          setIsAllMatch={setIsAllMatch}
          dataSource={dataSource}
        ></RecallSearch>
      </When>
    </div>
  );
}
